import React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, IReactionDisposer } from 'mobx';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { setPostImage } from '@images/imageUrls';
import Toast from '@components/Toast';
import Switch from '@components/switch';
import ImageCropperModal from '@components/ImageCropperModal/pc';
import { getBodyCoverImages, readCoverFile } from '@components/ImageCropperModal/pc/coverImages';
import TiptapEditor from './components/TiptapEditor';
import VoteModal from '@components/VoteModal/pc';
import FormModal from '@components/FormModal';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import './index.scss';
import { extractMentionsFromHtml } from '../../../../utils/setPostMentions';
import { isSupportedImageFile } from '../../../../utils/imageFile';

dayjs.locale('zh-cn');

/**
 * 判断定时发布时间是否已过期
 */
const isScheduleExpired = (fixedAt: string | Dayjs | null | undefined): boolean => {
    if (!fixedAt) return false;
    try {
        const fixedTime = dayjs(fixedAt);
        return fixedTime.isBefore(dayjs());
    } catch (e) {
        return false;
    }
};

/**
 * 获取初始化的发布状态
 * 如果定时时间已过期，重置为立即发布
 */
const getInitialPublishState = (fixedAt: string | Dayjs | null | undefined, publishMode: string): { publishMode: 'immediate' | 'scheduled'; fixedAt: Dayjs | null } => {
    if (!fixedAt) {
        return { publishMode: 'immediate', fixedAt: null };
    }

    if (isScheduleExpired(fixedAt)) {
        // 时间已过期，重置为立即发布
        return { publishMode: 'immediate', fixedAt: null };
    }

    // 时间未过期，保持定时发布
    try {
        return { 
            publishMode: 'scheduled', 
            fixedAt: dayjs(fixedAt) 
        };
    } catch (e) {
        return { publishMode: 'immediate', fixedAt: null };
    }
};

interface SetPostPcPageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
    setPostData: (data: any) => void;
    onPublishPost: (coverFile?: File | null, videoFile?: File | null) => void;
    topicList?: any[];
    categoryList?: string[];
    onCategoryChange?: (categoryName: string, page?: number) => void;
    loading?: boolean;
    whiteList?: string[]; // 白名单协议列表（deeplink）
    whiteHttpList?: string[]; // 白名单域名列表（HTTP/HTTPS）
    hasMore?: boolean;
    isLoadingMore?: boolean;
}
interface SetPostPCState extends BasePageState {
    showCropperModal: boolean;
    cropperImageSrc: string;
    coverBodyImages?: string[];
    cropperLoading: boolean;
    voteData: any;
    showVoteModal: boolean;
    coverFile: File | null;
    videoFile: File | null;
    collectionInput: string;
    collectionLoading: boolean;
    isAigc: boolean;
    hasCopyright: boolean; // 版权保护
    title: string; // 本地管理标题输入
    hasDraft: boolean; // 是否有草稿
    restoreDraftLoading: boolean; // 恢复草稿加载中
    saveDraftLoading: boolean; // 存草稿加载中
    formModalVisible: boolean; // 表单弹框可见性
    publishMode: 'immediate' | 'scheduled'; // 发布模式：立即发布/定时发布
    fixedAt: Dayjs | null; // 定时发布时间
    expandInfoExpanded: boolean; // 扩展信息展开状态
}

@inject('app', 'site', 'user', 'thread', 'setPost')
@observer
class SetPostPc extends BasePage<SetPostPcPageProps, SetPostPCState> {
    fileInputRef = React.createRef<HTMLInputElement>();
    titleDisposer: IReactionDisposer | null = null;
    publishModeDisposer: IReactionDisposer | null = null; // 🔧 新增：监听发布时间变化
    copyrightDisposer: IReactionDisposer | null = null;
    aigcDisposer: IReactionDisposer | null = null;

    state: SetPostPCState = {
        loading: false,
        error: null,
        pageLoaded: false,
        showCropperModal: false,
        cropperImageSrc: '',
        cropperLoading: false,
        voteData: null,
        showVoteModal: false,
        // 保存文件对象用于上传
        coverFile: null,
        videoFile: null,
        collectionInput: '',
        collectionLoading: false,
        isAigc: false,
        hasCopyright: false,
        title: '', // 本地管理标题输入
        hasDraft: false, // 是否有草稿
        restoreDraftLoading: false, // 恢复草稿加载中
        saveDraftLoading: false, // 存草稿加载中
        formModalVisible: false, // 表单弹框可见性
        publishMode: 'immediate', // 默认立即发布
        fixedAt: null, // 定时发布时间
        expandInfoExpanded: false, // 扩展信息展开状态
    };
    componentDidMount() {
        super.componentDidMount();
        this.initializePublishState();

        const store = this.props.setPost;
        if (store?.postData) {
            this.titleDisposer = reaction(
                () => store.postData?.title,
                (newTitle) => {
                    this.setState({ title: newTitle || '' });
                }
            );

            // 编辑详情或草稿异步加载后，同步内容声明开关的回显状态。
            this.aigcDisposer = reaction(
                () => store.postData?.isAigc,
                (isAigc) => this.setState({ isAigc: !!isAigc }),
                { fireImmediately: true }
            );

            this.copyrightDisposer = reaction(
                () => store.postData?.hasCopyright,
                (hasCopyright) => {
                    this.setState({ hasCopyright: !!hasCopyright });
                },
                { fireImmediately: true }
            );

            // 🔧 新增：监听 publishMode 和 fixedAt 变化，使用 fireImmediately 确保首次也触发
            this.publishModeDisposer = reaction(
                () => ({
                    publishMode: store.postData?.publishMode,
                    fixedAt: store.postData?.fixedAt,
                }),
                ({ publishMode, fixedAt }) => {
                    console.log('[SetPostPc] reaction triggered:', { publishMode, fixedAt });
                    let newPublishMode: 'immediate' | 'scheduled' = 'immediate';
                    let fixedAtState = null;

                    // 修复：只要有 fixedAt，就认为是定时发布
                    if (fixedAt) {
                        try {
                            fixedAtState = dayjs(fixedAt);
                            newPublishMode = 'scheduled';
                        } catch (e) {
                            fixedAtState = null;
                            newPublishMode = 'immediate';
                        }
                    } else if (publishMode === 'scheduled') {
                        // 如果没有 fixedAt 但 publishMode 是 scheduled，仍然设置为 scheduled（可能用户还没选时间）
                        newPublishMode = 'scheduled';
                    }

                    this.setState({
                        publishMode: newPublishMode,
                        fixedAt: fixedAtState,
                    });
                },
                { fireImmediately: true } // 🔧 关键：首次立即执行
            );
        }

        // 页面初始化时查询是否有草稿
        this.checkDraft();
    }

    /**
     * 初始化发布状态（单独提取为方法，便于复用）
     */
    initializePublishState = () => {
        const initAigc = this.props.setPost?.postData?.isAigc ?? false;
        const initCopyright = this.props.setPost?.postData?.hasCopyright ?? false;
        const initTitle = this.props.setPost?.postData?.title || '';
        
        // 初始化发布时间，检查是否已过期
        const initPublishMode = this.props.setPost?.postData?.publishMode || 'immediate';
        const initFixedAt = this.props.setPost?.postData?.fixedAt;
        
        // 使用工具函数检查时间是否过期
        const { publishMode: finalPublishMode, fixedAt: finalFixedAt } = getInitialPublishState(initFixedAt, initPublishMode);
        
        this.setState({ 
            isAigc: initAigc, 
            hasCopyright: initCopyright,
            title: initTitle,
            publishMode: finalPublishMode,
            fixedAt: finalFixedAt,
        });
    }

    componentDidUpdate(prevProps: any, prevState: any) {
        const currentTopicListLength = this.props.topicList?.length || 0;
        const prevTopicListLength = prevProps?.topicList?.length || 0;

        if (currentTopicListLength === 0 && prevTopicListLength > 0) {
            // topicList 从有数据变为空
        }
        if (currentTopicListLength > 0 && prevTopicListLength === 0) {
            // topicList 从空变为有数据
        }

        // ✅ 关键修复：直接从 store 读取当前值，而不是依赖 props 比较
        // 因为 MobX inject 的 store 是同一个引用，props 比较可能失效
        const store = this.props.setPost;
        const currFixedAt = store?.postData?.fixedAt;
        const currPublishMode = store?.postData?.publishMode;
        
        // 检查 state 和 store 是否不同步
        const stateFixedAt = this.state.fixedAt ? this.state.fixedAt.format('YYYY-MM-DD HH:mm:ss') : null;
        const storeFixedAtStr = currFixedAt || null;
        
        // 如果 store 有 fixedAt 但 state 没有（或值不同），同步
        if (currFixedAt && stateFixedAt !== storeFixedAtStr) {
            console.log('[SetPostPc] componentDidUpdate - syncing fixedAt from store:', {
                storeFixedAt: currFixedAt,
                stateFixedAt: stateFixedAt,
            });
            try {
                const fixedAtDayjs = dayjs(currFixedAt);
                this.setState({
                    publishMode: 'scheduled',
                    fixedAt: fixedAtDayjs,
                });
            } catch (e) {
                console.error('[SetPostPc] Failed to parse fixedAt:', e);
            }
        } else if (!currFixedAt && currPublishMode === 'scheduled' && this.state.publishMode !== 'scheduled') {
            // store 是 scheduled 但没有 fixedAt，state 也要同步为 scheduled
            this.setState({ publishMode: 'scheduled' });
        }
    }

    componentWillUnmount() {
        // 清理 reaction
        if (this.titleDisposer) {
            this.titleDisposer();
        }
        // 🔧 新增：清理发布时间 reaction
        if (this.publishModeDisposer) {
            this.publishModeDisposer();
        }
        if (this.copyrightDisposer) {
            this.copyrightDisposer();
        }
        // 页面卸载时释放内容声明监听，避免继续更新已卸载的页面。
        if (this.aigcDisposer) {
            this.aigcDisposer();
        }
    }

    handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        this.setState({ title });
        // 同步到 MobX store
        this.props.setPostData({ title });
    }

    handleContentChange = (content: string) => {
        const parsedMentions = extractMentionsFromHtml(content);
        this.props.setPostData({ content, mentions: parsedMentions });
    }

    handleCollectionInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ collectionInput: e.target.value });
    }

    handleAddCollection = async () => {
        const { setPost } = this.props;
        const input = (this.state.collectionInput || '').trim();
        if (!input) {
            Toast.info('请输入帖子链接');
            return;
        }

        // 按行分割，去空行
        const links = input.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        if (links.length === 0) {
            Toast.info('请输入有效的帖子链接');
            return;
        }

        const currentCount = setPost?.postData?.collection?.length || 0;
        if (currentCount + links.length > 10) {
            Toast.info(`最多添加10个关联帖子，当前已有${currentCount}个`);
            return;
        }

        this.setState({ collectionLoading: true });
        let successCount = 0;
        let failCount = 0;

        // 逐个添加
        for (const link of links) {
            const ret = setPost?.fetchCollectionThread ? await setPost?.fetchCollectionThread(link) : { code: -1, message: '缺少接口方法' };
            if (ret.code === 0) {
                successCount++;
            } else {
                failCount++;
            }
        }

        this.setState({ collectionLoading: false });

        if (successCount > 0) {
            this.setState({ collectionInput: '' });
            const msg = failCount > 0 ? `成功添加${successCount}个，失败${failCount}个` : `成功添加${successCount}个关联帖子`;
            Toast.success(msg);
        } else {
            Toast.info('批量添加失败，请检查链接');
        }
    }

    handleRemoveCollection = (resourceId: number) => {
        const { setPost } = this.props;
        const id = Number(resourceId);
        setPost?.removeCollectionItem && setPost.removeCollectionItem(id);
        // 强制触发渲染
        this.forceUpdate();
    }

    // 封面图上传相关方法
    handleCoverImageClick = () => {
        const images = getBodyCoverImages(this.props.setPost?.postData?.content || '');
        if (images.length) {
            this.setState({ showCropperModal: true, cropperImageSrc: '', coverBodyImages: images });
        } else {
            this.fileInputRef.current?.click();
        }
    }

    handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        // 验证文件类型
        if (!isSupportedImageFile(file)) {
            Toast.error('请选择图片文件');
            return;
        }

        // 验证文件大小 (限制 50MB)
        if (file.size > 50 * 1024 * 1024) {
            Toast.error('图片大小不能超过 50MB');
            return;
        }

        // 读取文件并显示裁剪弹窗
        const reader = new FileReader();
        reader.onload = (event) => {
            this.setState({
                cropperImageSrc: event.target?.result as string,
                showCropperModal: true,
                coverBodyImages: [],
            });
        };
        reader.onerror = () => Toast.error('图片读取失败，请重试');
        reader.readAsDataURL(file);

    }

    handleCropperConfirm = async (file: File) => {
        this.setState({ cropperLoading: true });

        try {
            const { setPost } = this.props;
            const imageUrl = await readCoverFile(file);
            setPost?.setCoverImage(imageUrl);
            this.setState({ coverFile: file, showCropperModal: false, cropperImageSrc: '', cropperLoading: false });
            Toast.success('封面设置成功');
        } catch (error) {
            this.setState({ cropperLoading: false });
            throw error;
        }
    }

    handleCropperCancel = () => {
        this.setState({
            showCropperModal: false,
            cropperImageSrc: '',
        });
    }

    handleRemoveCover = () => {
        const { setPost } = this.props;
        setPost?.setPostData({
            coverImage: '',
            coverId: 0,
            coverImageUploaded: false,
        });
        this.setState({ coverFile: null });
    }

    handleVideoUpload = (videoSrc: string, videoFile?: File, videoData?: any) => {
        const { setPost } = this.props;

        // 使用 OSS 返回的真实视频 URL
        const realVideoUrl = videoData?.url || videoData?.ossVideoUrl || videoSrc;
        const videoId = videoData?.id;

        if (setPost && setPost.setVideo) {
            setPost.setVideo(realVideoUrl);
        }

        if (videoId) {
            setPost.setPostData({
                videoId: videoId,
            });
        }

        // 如果已上传视频，保存视频数据
        if (videoFile) {
            this.setState({ videoFile });
        }

        if (videoData && videoData.videoCoverUrl) {
            if (setPost) {
                setPost.setPostData({
                    videoCoverUrl: videoData.videoCoverUrl, // 保存视频封面 URL
                    videoCoverData: videoData, // 保存完整视频数据
                });
            }
        }
    }

    handleRemoveVideo = () => {
        const { setPost } = this.props;
        if (setPost && setPost.setVideo) {
            setPost.setVideo('');
            // 同时清空 videoId 和相关视频信息
            setPost.setPostData({
                videoId: 0,
                videoCoverUrl: '',
            });
        }
        this.setState({ videoFile: null });
    }

    handleVoteSave = (voteData) => {
        const { setPost } = this.props;
        // 如果是数组，说明支持多投票
        if (Array.isArray(voteData)) {
            if (setPost && setPost.setVotes) {
                setPost.setVotes(voteData);
            } else {
                this.props.setPostData({ votes: voteData });
            }
        } else {
            // 单个投票，转换为数组
            if (setPost && setPost.setVotes) {
                setPost.setVotes([voteData]);
            } else {
                this.props.setPostData({ vote: voteData });
            }
        }
        this.setState({ voteData, showVoteModal: false });
    }

    handleRemoveVote = () => {
        const { setPost } = this.props;
        // 清空所有投票
        if (setPost && setPost.setVotes) {
            setPost.setVotes([]);
        } else {
            this.props.setPostData({ vote: null });
        }
        this.setState({ voteData: null });
    }

    handleVoteBoxClick = () => {
        this.setState({ showVoteModal: true });
    }

    handleVoteModalClose = () => {
        this.setState({ showVoteModal: false });
    }

    handleVoteModalOpen = () => {
        this.setState({ showVoteModal: true });
    }

    // 新增：话题选择回调，自动同步到帖子数据
    handleTopicSelect = (topics) => {
        const { setPost, topicList } = this.props;
        const topicIds = topics.map(t => t.id);

        // 更新话题缓存，确保话题信息持久化，特别是分类信息
        if (setPost && topics.length > 0) {
            const topicCache = setPost.topicCache || {};
            topics.forEach(topic => {
                if (topic.id && topic.name) {
                    // 如果话题没有分类信息，尝试从当前话题列表中获取
                    if (!topic.category && topicList && topicList.length > 0) {
                        const fullTopic = topicList.find(t => String(t.id) === String(topic.id));
                        if (fullTopic && fullTopic.category) {
                            topic.category = fullTopic.category;
                        }
                    }
                    topicCache[String(topic.id)] = { ...topic };
                }
            });
            setPost.topicCache = topicCache;
        }

        setPost.setPostData({ topicIds });
    };

    handleOpenFormModal = () => {
        this.setState({
            formModalVisible: true 
        }, () => {
            // 禁用滚动
            document.body.style.position = 'fixed';
            document.body.style.top = `0`;
            document.body.style.left = `0`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } );
    }

    handleCloseFormModal = () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        this.setState({ formModalVisible: false });
    }

    /**
     * 获取转换后的表单数据用于回显
     * 将后端返回的格式转换为 FormModal 组件需要的格式
     */
    getFormInitialData = () => {
        const { setPost } = this.props;
        const formData = setPost?.postData?.form;
        
        if (!formData || !formData.fields) {
            return { name: '', fields: [], startAt: '', endAt: '' };
        }

        // 转换 fields 数据格式
        const transformedFields = formData.fields.map((field: any) => {
            const transformed: any = {
                title: field.name || field.title || '', // 后端的 name 对应 FormModal 的 title
                type: field.type,
                isRequired: field.isRequired || false,
            };

            // 处理 options: 从对象数组 [{content: "选项"}] 转换为字符串数组 ["选项"]
            if (field.type === 'radio' || field.type === 'checkbox') {
                if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                    if (typeof field.options[0] === 'object' && field.options[0].content) {
                        // 后端格式: [{content: "选项"}]
                        transformed.options = field.options.map((opt: any) => opt.content);
                    } else {
                        // 已经是字符串数组格式
                        transformed.options = field.options;
                    }
                } else {
                    // 确保 options 至少是空数组
                    transformed.options = [];
                }
            }

            return transformed;
        });

        return {
            name: formData.name || '',
            fields: transformedFields,
            startAt: formData.startAt || '',
            endAt: formData.endAt || '',
        };
    }

    handleFormModalSave = (formData: any) => {
        const { setPost } = this.props;
        const transformedFields = (formData?.form || []).map((field: any) => {
            const transformed: any = {
                name: field.title, 
                type: field.type,
                isRequired: field.isRequired,
            };
            
            // 处理 options：从数组转换为对象数组
            if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                transformed.options = field.options.map((opt: any) => ({
                    content: typeof opt === 'string' ? opt : opt.title
                }));
            }
            
            return transformed;
        });
        
        const formPayload = {
            name: formData?.formTitle || '',
            fields: transformedFields,
            startAt: formData?.startAt || '',
            endAt: formData?.endAt || '',
        };

        if (setPost) {
            if (setPost.setPostData) {
                setPost.setPostData({ form: formPayload });
            } else if (this.props.setPostData) {
                this.props.setPostData({ form: formPayload });
            }
            Toast.success('表单已保存');
        }

        this.setState({ formModalVisible: false }, () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        });
    }

    handleAigcChange = (checked: boolean) => {
        const { setPost } = this.props;
        this.setState({ isAigc: checked });
        setPost?.setPostData && setPost.setPostData({ isAigc: checked });
    }

    handleCopyrightChange = (checked: boolean) => {
        const { setPost } = this.props;
        this.setState({ hasCopyright: checked });
        setPost?.setPostData && setPost.setPostData({ hasCopyright: checked });
    }

    // 将标题限制为最多40个字符，超出部分用省略号表示
    truncateTitle = (text?: string | null) => {
        if (!text) return '';
        const max = 33;
        return text.length > max ? text.slice(0, max) + '...' : text;
    }

    /**
     * 查询是否有草稿
     */
    checkDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.checkDraft) return;

        const ret = await setPost.checkDraft();
        if (ret.code === 0 && ret.data?.hasDraft) {
            this.setState({ hasDraft: true });
        }
    }

    /**
     * 恢复草稿
     */
    handleRestoreDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.restoreDraft || !setPost?.draftData?.draftThreadId) {
            Toast.error('无法恢复草稿');
            return;
        }

        this.setState({ restoreDraftLoading: true });
        try {
            const ret = await setPost.restoreDraft(setPost.draftData.draftThreadId);
            if (ret.code === 0) {
                Toast.success('草稿已恢复');
                this.setState({ hasDraft: false });

                // 同步发布时间到页面 state，检查时间是否已过期
                const storePublishMode = setPost.postData?.publishMode;
                const storeFixedAt = setPost.postData?.fixedAt;
                
                // 使用工具函数检查时间是否过期
                const { publishMode: finalPublishMode, fixedAt: finalFixedAt } = getInitialPublishState(storeFixedAt, storePublishMode);
                
                this.setState({
                    publishMode: finalPublishMode,
                    fixedAt: finalFixedAt,
                });
            } else {
                Toast.error(ret.message || '恢复草稿失败');
            }
        } catch (error) {
            Toast.error('恢复草稿出错');
        } finally {
            this.setState({ restoreDraftLoading: false });
        }
    }

    /**
     * 手动保存草稿
     */
    handleSaveDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.saveDraftManually) {
            Toast.error('缺少保存方法');
            return;
        }

        this.setState({ saveDraftLoading: true });
        try {
            // 同步发布设置到 mobx store，确保草稿保存时包含定时发布时间
            const fixedAtValue = this.state.publishMode === 'scheduled' && this.state.fixedAt
                ? this.state.fixedAt.format('YYYY-MM-DD HH:mm:ss')
                : null;
            setPost.setPostData({
                publishMode: this.state.publishMode,
                fixedAt: fixedAtValue,
            });
            const ret = await setPost.saveDraftManually(this.state.coverFile, this.state.videoFile);
            if (ret.code === 0) {
                Toast.success('草稿已保存');
            } else {
                Toast.error(ret.message || '保存草稿失败');
            }
        } catch (error) {
            Toast.error('保存草稿出错');
        } finally {
            this.setState({ saveDraftLoading: false });
        }
    }

    render() {
        const { setPost, topicList = [], categoryList: categoryListProp, onCategoryChange, hasMore, isLoadingMore } = this.props;
        const postData = setPost?.postData;
        const { collectionInput, collectionLoading, title } = this.state;
        const displayTitle = setPost?.postData?.title !== undefined ? setPost.postData.title : title;
        const categoryList = categoryListProp || [];
        return (
            <div className={styles.wrapper}>
                <div className={styles.formContainer}>
                    {Number(setPost?.postData?.type) === 0 && (
                        <input
                            className={styles.titleInput}
                            placeholder="标题（30字以内）"
                            type="text"
                            maxLength={30}
                            value={displayTitle}
                            onChange={this.handleTitleChange}
                        />
                    )}
                    {/* TipTap 富文本编辑器及自定义工具栏 */}
                    <TiptapEditor
                        content={setPost?.postData?.content || ''}
                        onContentChange={this.handleContentChange}
                        onVideoUpload={this.handleVideoUpload}
                        onVoteCreate={this.handleVoteSave}
                        showVoteModal={this.state.showVoteModal}
                        onVoteModalClose={this.handleVoteModalClose}
                        onVoteModalOpen={this.handleVoteModalOpen}
                        voteData={this.state.voteData}
                        topicList={topicList}
                        categoryList={categoryList}
                        onTopicSelect={this.handleTopicSelect}
                        onCategoryChange={onCategoryChange}
                        selectedTopicIds={setPost?.postData?.topicIds || []}
                        topicCache={setPost?.topicCache || {}}
                        hasVideo={!!setPost?.postData?.video}
                        whiteList={this.props.whiteList || []}
                        whiteHttpList={this.props.whiteHttpList || []}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                        otherPermissions={this.props.site?.otherPermissions}
                        currentMentions={extractMentionsFromHtml(setPost?.postData?.content || '')}
                        currentUserInfo={this.props.user?.userInfo}
                        emojiList={this.props.site?.emojiList}
                    />
                    {/* 视频预览区 */}
                    {setPost?.postData?.video && (
                        <div className={styles.videoPreviewBox}>
                            <video
                                src={setPost.postData.video}
                                className={styles.videoPreview}
                                controls
                            />
                            <img
                                src={setPostImage.delete}
                                alt="删除视频"
                                className={styles.videoDeleteIcon}
                                style={{ width: 20, height: 20 }}
                                onClick={this.handleRemoveVideo}
                            />
                        </div>
                    )}
                    {/* 投票样式区（回显所有投票） */}
                    {setPost?.postData?.votes && setPost.postData.votes.length > 0 && setPost.postData.votes.map((vote, idx) => {
                        // 计算倒计时显示
                        let countdownText = '';
                        
                        // 优先使用 duration，如果没有则从 expiredAt 或 endTime 计算
                        if (vote.duration) {
                            const { days = 0, hours = 0, minutes = 0 } = vote.duration;
                            countdownText = `${days} 天 ${hours} 时 ${minutes} 分`;
                        } else if (vote.expiredAt || vote.endTime) {
                            // 从结束时间计算倒计时
                            const endTime = vote.expiredAt || vote.endTime;
                            try {
                                const endDate = new Date(endTime);
                                const now = new Date();
                                const diffTime = endDate.getTime() - now.getTime();
                                
                                if (diffTime > 0) {
                                    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                    const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
                                    countdownText = `${days} 天 ${hours} 时 ${minutes} 分`;
                                }
                            } catch (error) {
                                // 计算倒计时失败时忽略错误
                            }
                        }
                        
                        return (
                            <div className={styles.voteBox} key={idx} onClick={this.handleVoteBoxClick}>
                                <div className={styles.voteIconWrapper}>
                                    <img
                                        src={setPostImage.voteActive}
                                        alt="vote"
                                        className={styles.voteIcon}
                                    />
                                </div>
                                <div className={styles.voteContent}>
                                    <div className={styles.voteTitle}>{this.truncateTitle(vote.title || vote.question)}</div>
                                    {countdownText && (
                                        <div className={styles.voteCountdown}>
                                            距结束：<span className={styles.voteCountdownNum}>{countdownText}</span>
                                        </div>
                                    )}
                                </div>
                                <img
                                    src={setPostImage.delete}
                                    alt="close"
                                    className={styles.voteCloseBtn}
                                    style={{ width: 20, height: 20 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleRemoveVote();
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
                {/* 封面图模块独立管理，只显示用户设置的帖子封面 */}
                {(() => {
                    const previewCover = setPost?.postData?.coverImage;

                    return (
                        <div className={styles.expandInfoBox}>
                            <div className={styles.expandInfoTitle} style={{marginBottom: '30px'}}>设置封面</div>
                            <div className={styles.coverImageSection}>
                                {/* 显示独立封面的预览或上传按钮 */}
                                {previewCover ? (
                                    <div className={styles.coverImageWrapper}>
                                        <img
                                            src={previewCover}
                                            alt="封面"
                                            role="button"
                                            tabIndex={0}
                                            aria-label="重新设置封面"
                                            onClick={this.handleCoverImageClick}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleCoverImageClick(); } }}
                                            style={{ cursor: 'pointer' }}
                                            className={styles.coverPreviewImg}
                                        />
                                        <img
                                            src={setPostImage.deleteCover}
                                            alt="删除"
                                            className={styles.coverDeleteIcon}
                                            onClick={this.handleRemoveCover}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.coverImageUpload} onClick={this.handleCoverImageClick}>
                                        <img
                                            src={setPostImage.addCover}
                                            alt="上传封面"
                                            className={styles.uploadIcon}
                                        />
                                    </div>
                                )}
                                <input
                                    ref={this.fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={this.handleFileChange}
                                />
                            </div>
                        </div>
                    );
                })()}

                {/* 扩展信息 this.props.site?.otherPermissions?.canInsertThreadForm表单权限 */}
                <div className={styles.expandInfoBox}>
                    <div
                        className={styles.expandInfoTitle}
                        onClick={() => this.setState({ expandInfoExpanded: !this.state.expandInfoExpanded })}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                        扩展信息
                        <svg className={styles.expandInfoArrow + (this.state.expandInfoExpanded ? ' ' + styles.expandInfoArrowUp : '')} viewBox="0 0 1024 1024" width="16" height="16" fill="#262626"><path d="M917.4 302.8c-14.2-14.2-37.2-14.2-51.4 0L518.6 650.3c-2.9 2.8-7.5 2.8-10.3 0L160.8 302.8c-14.2-14.2-37.2-14.2-51.4 0-14.2 14.2-14.2 37.2 0 51.4l347.4 347.4c15.6 15.6 36 23.4 56.5 23.4s41-7.8 56.5-23.4l347.4-347.4c14.3-14.2 14.3-37.2 0.2-51.4z"/></svg>
                    </div>

                    {this.state.expandInfoExpanded && (<div className={styles.expandInfoContent}>
                        {/* 关联帖子 */}
                        <div className={styles.expandInfoSection}>
                            <div className={styles.expandInfoRow}>
                                <div className={styles.expandInfoLabel}>关联帖子</div>
                                <textarea
                                    placeholder="请输入vivo社区帖子链接，按行分割"
                                    className={styles.expandInput}
                                    style={{ minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
                                    value={collectionInput}
                                    onChange={this.handleCollectionInputChange}
                                />
                                <button
                                    className={styles.addBtn}
                                    onClick={this.handleAddCollection}
                                    disabled={collectionLoading}
                                >
                                    {collectionLoading ? '添加中...' : '批量添加'}
                                </button>
                                <div className={styles.expandInfoTip}>只支持vivo社区链接，最多添加10个，每行一个链接</div>
                            </div>

                            
                            <div className={styles.postList}>
                                {(setPost?.postData?.collection || []).map((item: any) => (
                                    <div className={styles.postItem} key={item.resource_id || item.id}>
                                        {item.cover && item.cover.url ? (
                                            <img
                                                src={item.cover.url}
                                                alt="post"
                                                className={styles.postItemImg}
                                            />
                                        ) : item.cover_url ? (
                                            <img
                                                src={item.cover_url}
                                                alt="post"
                                                className={styles.postItemImg}
                                            />
                                        ) : item.images && item.images.length > 0 ? (
                                            <img
                                                src={item.images[0].url}
                                                alt="post"
                                                className={styles.postItemImg}
                                            />
                                        ) : null}
                                        <div className={styles.postItemContent}>
                                            {item.title && (
                                                <div className={styles.postItemTitle}>{item.title}</div>
                                            )}
                                            {
                                                item.summary && <div className={styles.postItemDesc}>{item.summary || '暂无概述'}</div>
                                            }
                                        </div>
                                        <img
                                            src={setPostImage.close}
                                            alt="delete"
                                            className={styles.postItemDelete}
                                            onClick={() => this.handleRemoveCollection(item.resource_id || item.id)}
                                        />
                                    </div>
                                ))}
                            </div> 
                        </div>

                        {/* 创建表单 */}
                        {this.props.site?.otherPermissions?.canInsertThreadForm && (
                            <div className={styles.expandInfoSection}>
                                <div className={styles.expandInfoRow}>
                                    <div className={styles.expandInfoLabel}>创建表单</div>
                                    <div
                                        className={styles.addBtn}
                                        onClick={this.handleOpenFormModal}
                                    >
                                        {setPost?.postData?.form ? '编辑表单' : '填写表单内容'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>)}
                </div>

                {/* 内容设置 */}
                <div className={styles.contentSettingBox}>
                    <div className={styles.contentSettingTitle}>内容设置</div>
                    {/* <div className={styles.contentSettingRow}>
                        <div className={styles.contentSettingLabel}>谁可以看</div>
                        <select className={styles.contentSettingSelect} defaultValue="公开">
                            <option value="公开">公开</option>
                            <option value="仅自己">仅自己</option>
                            <option value="仅好友">仅好友</option>
                        </select>
                    </div>
                    <div className={styles.contentSettingRow}>
                        <div className={styles.contentSettingLabel}>保存权限</div>
                        <label className={styles.contentSettingSwitchLabel}>
                            <input type="checkbox" className={styles.contentSettingSwitch} />
                            <span className={styles.contentSettingSwitchSlider}></span>
                        </label>
                        <span className={styles.contentSettingSwitchText}>允许他人下载图片</span>
                    </div> */}
                  
                    <div className={styles.contentSettingRow}>
                        <div className={styles.contentSettingLabel}>版权保护</div>
                        <Switch
                            checked={!!postData?.hasCopyright}
                            onChange={this.handleCopyrightChange}
                            className={styles.contentSettingSwitchControl}
                            aria-label="允许他人下载图片"
                        />
                        <span className={styles.contentSettingSwitchText}>不允许他人下载图片</span>
                    </div>
                      <div className={styles.contentSettingRow}>
                        <div className={styles.contentSettingLabel}>内容声明</div>
                        <Switch
                            checked={this.state.isAigc}
                            onChange={this.handleAigcChange}
                            className={styles.contentSettingSwitchControl}
                            aria-label="内容含AI生成元素"
                        />
                        <span className={styles.contentSettingSwitchText}>内容含AI生成元素</span>
                    </div>
                    {
                        this.props.site?.otherPermissions?.canInsertFixedAt&&<div className={styles.contentSettingRow}>
                        <div className={styles.contentSettingLabel}>发布时间</div>
                        <div className={styles.publishModeOptions}>
                            {(() => {
                                // 判断是否可以修改发布模式
                                // 定时发布的帖子（有fixedAt且时间未过期）可以修改
                                // 只有已正式发布的帖子（非定时或定时已过且已发布）才禁用
                                const hasId = !!setPost?.postData?.id;
                                const isDraft = !!setPost?.postData?.isDraft;
                                const currentFixedAt = setPost?.postData?.fixedAt;
                                
                                // 判断当前定时时间是否未过期（即帖子还在等待定时发布）
                                const isWaitingScheduled = currentFixedAt && !isScheduleExpired(currentFixedAt);
                                
                                // 可以修改的情况：
                                // 1. 新建帖子（无ID）
                                // 2. 草稿
                                // 3. 定时发布但时间未到的帖子（可以改成立即发布或修改定时时间）
                                const canModifyPublishMode = !hasId || isDraft || isWaitingScheduled;
                                
                                return (
                                    <>
                                        <div
                                            className={styles.publishModeItem}
                                            onClick={() => canModifyPublishMode && this.setState({ publishMode: 'immediate' })}
                                            style={!canModifyPublishMode ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                        >
                                            <img
                                                src={this.state.publishMode === 'immediate' ? setPostImage.checkedActive : setPostImage.checked}
                                                alt=""
                                                className={styles.publishModeIcon}
                                            />
                                            <span className={styles.publishModeText}>立即发布</span>
                                        </div>
                                        <div
                                            className={styles.publishModeItem}
                                            onClick={() => canModifyPublishMode && this.setState({ publishMode: 'scheduled' })}
                                            style={!canModifyPublishMode ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                        >
                                            <img
                                                src={this.state.publishMode === 'scheduled' ? setPostImage.checkedActive : setPostImage.checked}
                                                alt=""
                                                className={styles.publishModeIcon}
                                            />
                                            <span className={styles.publishModeText}>定时发布</span>
                                        </div>
                                        {this.state.publishMode === 'scheduled' && (
                                            <DatePicker
                                                showTime
                                                value={this.state.fixedAt}
                                                onChange={(date) => {
                                                    if (!canModifyPublishMode) return;
                                                    const fixedAtStr = date ? date.format('YYYY-MM-DD HH:mm:ss') : null;
                                                    // 同时更新本地 state 和 store，确保 reaction 能够读取修改后的值
                                                    this.setState({ fixedAt: date });
                                                    setPost?.setPostData && setPost.setPostData({ fixedAt: fixedAtStr });
                                                }}
                                                placeholder="选择发布时间"
                                                className={styles.publishDatePicker}
                                                format="YYYY-MM-DD HH:mm"
                                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                                                disabled={!canModifyPublishMode}
                                            />
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    }
                    
                </div>
                <div style={{height:'1px'}}></div>
                {/* 底部固定操作区 */}
                <div className={styles.fixedFooterBar}>
                    <div className={styles.fixedFooterBarContent}>
                        <div className={styles.footerBtnGroup}>
                            {/* 仅正式编辑态隐藏草稿按钮；恢复草稿后仍可继续保存草稿 */}
                            {(() => {
                                const isRestoredDraft = !!this.props.setPost?.draftData?.isRestoreDraft || !!this.props.setPost?.postData?.isDraft;
                                const isEditingPublishedPost = !!this.props.setPost?.postData?.id && !isRestoredDraft;
                                return isEditingPublishedPost ? null : (
                                    <>
                                        <button
                                            className={styles.outlineBtn}
                                            onClick={this.handleRestoreDraft}
                                            disabled={!this.state.hasDraft || this.state.restoreDraftLoading}
                                        >
                                            {this.state.restoreDraftLoading ? '恢复中...' : '恢复草稿'}
                                        </button>
                                        <button
                                            className={styles.outlineBtn}
                                            onClick={this.handleSaveDraft}
                                            disabled={this.state.saveDraftLoading}
                                        >
                                            {this.state.saveDraftLoading ? '保存中...' : '存草稿'}
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                        <div className={styles.footerBtnGroup}>
                            <button className={styles.outlineBtn} style={{ opacity: 0 }}>预览</button>
                            <button
                                className={styles.gradientBtn}
                                onClick={() => {
                                    if (!this.props.loading) {
                                        // 同步发布设置到 mobx store
                                        const fixedAtValue = this.state.publishMode === 'scheduled' && this.state.fixedAt
                                            ? this.state.fixedAt.format('YYYY-MM-DD HH:mm:ss')
                                            : null;
                                        this.props.setPostData({
                                            publishMode: this.state.publishMode,
                                            fixedAt: fixedAtValue,
                                        });
                                        // 视频已在选择时上传到 OSS，发布时只需上传手动选择的封面
                                        this.props.onPublishPost(this.state.coverFile, null);
                                    }
                                }}
                                disabled={this.props.loading}
                            >
                                {this.props.loading 
                                    ? (this.state.publishMode === 'scheduled' && this.state.fixedAt ? '定时发布中...' : '发布中...')
                                    : (this.state.publishMode === 'scheduled' && this.state.fixedAt ? '定时发布' : '发布')
                                }
                            </button>
                        </div>
                    </div>
                </div>
                {/* 图片裁剪弹窗 */}
                <ImageCropperModal
                    visible={this.state.showCropperModal || false}
                    imageSrc={this.state.cropperImageSrc || ''}
                    bodyImages={this.state.coverBodyImages || []}
                    title={setPost?.postData?.title}
                    summary={(setPost?.postData?.content || '').replace(/<[^>]*>/g, '')}
                    author={this.props.user?.nickname}
                    avatar={this.props.user?.avatar}
                    onConfirm={this.handleCropperConfirm}
                    onCancel={this.handleCropperCancel}
                    loading={this.state.cropperLoading || false}
                />
                {/* 投票弹窗，支持回显和编辑 */}
                <VoteModal
                    visible={this.state.showVoteModal}
                    votes={setPost?.postData?.votes || []}
                    onSave={this.handleVoteSave}
                    onCancel={this.handleVoteModalClose}
                />
                {/* ✅ 表单弹窗，支持回显和编辑 */}
                <FormModal
                    visible={this.state.formModalVisible}
                    initialData={this.getFormInitialData()}
                    userCount={setPost?.postData?.form?.userCount || 0}
                    onSave={this.handleFormModalSave}
                    onCancel={this.handleCloseFormModal}
                    onClose={this.handleCloseFormModal}
                />
            </div>
        );
    }
}

export default withRouter(SetPostPc);
